/** @file World drag results to a bone's local values: translation and rotation under its parent. */
import { affine } from '@pose2d/math/affine';
import { angle } from '@pose2d/math/angle';
import { trig } from '@pose2d/math/trig';
import { vec2 } from '@pose2d/math/vec2';
import type { Affine, Vec2 } from '@pose2d/math/types';
import { invertParent } from './_internal/invertParent';
import { unitAt } from './_internal/unitAt';

/**
 * Converts world-space gizmo results into local bone values; a null parent means a root (identity). A degenerate
 * parent (|det| < 1e-12, e.g. a zero scale) inverts its translation only, so results stay finite (R4-03).
 */
export const parentSpace = {
  /** Local (x, y) that puts the bone origin at world (wx, wy); pass the full parent world matrix. */
  point(parent: Affine | null, wx: number, wy: number, out: Vec2): Vec2 {
    if (parent === null) return vec2.set(out, wx, wy);
    return affine.apply(invertParent(new Float64Array(6), parent), wx, wy, out);
  },
  /**
   * Local rotation whose X axis points at world angle worldDeg (exact under any effective parent, given the bone's
   * shearX and a positive scaleX), unwrapped to the value nearest `near` so continuous drags never jump by 360.
   */
  rotation(effectiveParent: Affine | null, worldDeg: number, shearX: number, near: number): number {
    const d = unitAt(worldDeg, [0, 0]);
    if (effectiveParent !== null) affine.applyVec(invertParent(new Float64Array(6), effectiveParent), d[0], d[1], d);
    const raw = angle.radToDeg(trig.atan2(d[1], d[0])) - shearX;
    return near + angle.shortest(near, raw);
  },
};
